import type { WorkbookContext } from "../../../shared/account-finance/finance-workbook-contract";
import type { FinanceTimelineRow } from "./finance-workbook-timeline";

export type DisplayCurrency = "original" | "TL" | "USD" | "EUR";
export function convertWorkbookGroups(groups: { currency: string; rows: FinanceTimelineRow[] }[],
  to: DisplayCurrency, context: WorkbookContext | undefined, date: string) {
  if (to === "original" || groups.length === 0) return groups;
  const rows = groups[0].rows.map(row => ({ ...row, values: row.values.map((_, index) => {
    let total = 0n;
    for (const group of groups) {
      const source = group.rows.find(item => item.id === row.id)?.values[index] ?? null;
      const value = convertWorkbookAmount(source, group.currency, to, context, date);
      if (value === null) return null;
      total += value;
    }
    return total;
  }) }));
  const opening = rows.find(row => row.id === "opening");
  const net = rows.find(row => row.id === "net"), closing = rows.find(row => row.id === "closing");
  if (opening && net && closing) {
    let carry = opening.values[0] ?? null;
    for (let index = 0; index < opening.values.length; index++) {
      const movements = rows.filter(row => (row.kind === "incoming" || row.kind === "outgoing") && row.included !== false);
      const values = movements.map(row => row.values[index] ?? null);
      const change = values.some(value => value === null) ? null : values.reduce<bigint>((sum, value) => sum + (value ?? 0n), 0n);
      for (const kind of ["incoming", "outgoing"] as const) {
        const subtotal = rows.find(row => row.id === `${kind}-total`);
        const amounts = movements.filter(row => row.kind === kind).map(row => row.values[index] ?? null);
        if (subtotal) subtotal.values[index] = amounts.some(value => value === null) ? null : amounts.reduce<bigint>((sum, value) => sum + (value ?? 0n), 0n);
      }
      opening.values[index] = carry;
      net.values[index] = change;
      carry = carry === null || change === null ? null : carry + change;
      closing.values[index] = carry;
    }
  }
  return [{ currency: to, rows }];
}
export function convertWorkbookAmount(amount: bigint | null, from: string, to: DisplayCurrency,
  context: WorkbookContext | undefined, reportDate: string): bigint | null {
  if (amount === null || to === "original" || from === to) return amount;
  const rate = (currency: string) => currency === "TL" ? 1000000n : (() => {
    const entry = context?.rates.find(item => item.currency === currency && item.date === reportDate);
    return entry && BigInt(entry.tlPerUnit) > 0n ? BigInt(entry.tlPerUnit) : null;
  })();
  const source = rate(from), target = rate(to);
  if (source === null || target === null) return null;
  const numerator = amount * source;
  const magnitude = numerator < 0n ? -numerator : numerator;
  const rounded = (magnitude + target / 2n) / target;
  return numerator < 0n ? -rounded : rounded;
}
